import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/lib/api';

const StaffManagement = ({ outlets = [], selectedOutlet }) => {
  const [staff, setStaff] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [newStaff, setNewStaff] = useState({
    username: '',
    password: '',
    role: 'pos',
    full_name: '',
    phone: '',
    email: '',
    outlet_ids: [],
    active: true
  });

  useEffect(() => {
    fetchStaff();
  }, [selectedOutlet]);

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff');
      let staffData = response.data;
      
      // Filter by outlet if specific outlet is selected
      if (selectedOutlet && selectedOutlet !== 'all') {
        staffData = staffData.filter(s => 
          s.outlet_ids?.includes(selectedOutlet) || s.outlet_ids?.length === 0
        );
      }
      
      setStaff(staffData);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const handleAddStaff = async () => {
    if (!newStaff.full_name || !newStaff.username || !newStaff.password || !newStaff.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    
    // For non-admin roles, outlet_ids should not be empty (unless admin)
    if (newStaff.role !== 'admin' && newStaff.outlet_ids.length === 0) {
      toast.error('Please select at least one location for this staff member');
      return;
    }
    
    try {
      await api.post('/staff', newStaff);
      toast.success('Staff member added successfully');
      setShowAdd(false);
      setNewStaff({
        username: '',
        password: '',
        role: 'pos',
        full_name: '',
        phone: '',
        email: '',
        outlet_ids: [],
        active: true
      });
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    }
  };

  const handleEditStaff = async () => {
    // For non-admin roles, outlet_ids should not be empty
    if (editingStaff.role !== 'admin' && editingStaff.outlet_ids.length === 0) {
      toast.error('Please select at least one location for this staff member');
      return;
    }
    
    try {
      const updates = { ...editingStaff };
      delete updates.id;
      delete updates.username;
      delete updates.created_at;
      
      await api.put(`/staff/${editingStaff.id}`, updates);
      toast.success('Staff updated successfully');
      setShowEdit(false);
      setEditingStaff(null);
      fetchStaff();
    } catch (error) {
      toast.error('Failed to update staff');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    
    try {
      await api.delete(`/staff/${staffId}`);
      toast.success('Staff deleted');
      fetchStaff();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  const toggleOutletSelection = (outletId, isNew = true) => {
    if (isNew) {
      setNewStaff(prev => ({
        ...prev,
        outlet_ids: prev.outlet_ids.includes(outletId)
          ? prev.outlet_ids.filter(id => id !== outletId)
          : [...prev.outlet_ids, outletId]
      }));
    } else {
      setEditingStaff(prev => ({
        ...prev,
        outlet_ids: prev.outlet_ids.includes(outletId)
          ? prev.outlet_ids.filter(id => id !== outletId)
          : [...prev.outlet_ids, outletId]
      }));
    }
  };

  const getOutletName = (outletId) => {
    const outlet = outlets.find(o => o.id === outletId);
    return outlet ? outlet.name : outletId;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      manager: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      pos: 'bg-green-500/20 text-green-400 border-green-500/50',
      kitchen: 'bg-orange-500/20 text-orange-400 border-orange-500/50'
    };
    return colors[role] || colors.pos;
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      admin: 'Full access to all locations and modules',
      manager: 'Access to POS, Kitchen & Admin (location restricted)',
      pos: 'Access to POS System only',
      kitchen: 'Access to Kitchen Display only'
    };
    return descriptions[role] || '';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
            Staff Management
          </h2>
          <p className="text-gray-400 text-sm">Manage user accounts, roles, and location access</p>
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Staff
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {staff.map(member => (
          <Card key={member.id} className="bg-[#121212] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">{member.full_name}</h3>
                  <p className="text-sm text-gray-400">@{member.username}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingStaff({...member, outlet_ids: member.outlet_ids || []});
                      setShowEdit(true);
                    }}
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteStaff(member.id)}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-400">📞 {member.phone}</p>
                {member.email && <p className="text-sm text-gray-400">📧 {member.email}</p>}
              </div>

              <div className="flex items-center gap-2 mb-3">
                <Badge className={getRoleBadgeColor(member.role)}>
                  {member.role.toUpperCase()}
                </Badge>
                <Badge className={member.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                  {member.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {/* Location Access */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="flex items-center gap-1 mb-2">
                  <MapPin className="w-3 h-3 text-gray-500" />
                  <span className="text-xs text-gray-500 uppercase">Location Access</span>
                </div>
                {member.role === 'admin' ? (
                  <div className="text-xs text-[#00E5FF] font-medium">
                    All Locations (Admin)
                  </div>
                ) : member.outlet_ids && member.outlet_ids.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {member.outlet_ids.map(outletId => (
                      <Badge key={outletId} variant="outline" className="text-xs border-white/20 text-gray-300">
                        {getOutletName(outletId)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-yellow-400">
                    No locations assigned
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {staff.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400">No staff members found</p>
          <p className="text-gray-500 text-sm">Click "Add Staff" to create a new staff account</p>
        </div>
      )}

      {/* Add Staff Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Add Staff Member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">Full Name *</Label>
              <Input
                value={newStaff.full_name}
                onChange={(e) => setNewStaff({...newStaff, full_name: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Username *</Label>
              <Input
                value={newStaff.username}
                onChange={(e) => setNewStaff({...newStaff, username: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="johndoe"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Password *</Label>
              <Input
                type="password"
                value={newStaff.password}
                onChange={(e) => setNewStaff({...newStaff, password: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Role *</Label>
              <Select value={newStaff.role} onValueChange={(value) => setNewStaff({...newStaff, role: value, outlet_ids: value === 'admin' ? [] : newStaff.outlet_ids})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="admin">Admin (Full Access)</SelectItem>
                  <SelectItem value="manager">Manager (POS + Kitchen + Admin)</SelectItem>
                  <SelectItem value="pos">POS Staff</SelectItem>
                  <SelectItem value="kitchen">Kitchen Staff (KOT)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{getRoleDescription(newStaff.role)}</p>
            </div>
            
            {/* Location Selection - Only for non-admin roles */}
            {newStaff.role !== 'admin' && (
              <div>
                <Label className="text-white mb-2 block">Location Access *</Label>
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  {outlets.length === 0 ? (
                    <p className="text-gray-500 text-sm">No outlets available</p>
                  ) : (
                    outlets.map(outlet => (
                      <div key={outlet.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`new-outlet-${outlet.id}`}
                          checked={newStaff.outlet_ids.includes(outlet.id)}
                          onCheckedChange={() => toggleOutletSelection(outlet.id, true)}
                          className="border-white/30"
                        />
                        <label
                          htmlFor={`new-outlet-${outlet.id}`}
                          className="text-sm text-gray-300 cursor-pointer flex-1"
                        >
                          {outlet.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select the locations this staff member can access
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-white mb-2 block">Phone *</Label>
              <Input
                value={newStaff.phone}
                onChange={(e) => setNewStaff({...newStaff, phone: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="+91 9876543210"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Email</Label>
              <Input
                type="email"
                value={newStaff.email}
                onChange={(e) => setNewStaff({...newStaff, email: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="john@example.com"
              />
            </div>
            <Button
              onClick={handleAddStaff}
              className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6 text-lg font-bold"
            >
              Add Staff Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Edit Staff Member
            </DialogTitle>
          </DialogHeader>
          {editingStaff && (
            <div className="space-y-4">
              <div>
                <Label className="text-white mb-2 block">Full Name</Label>
                <Input
                  value={editingStaff.full_name}
                  onChange={(e) => setEditingStaff({...editingStaff, full_name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Role</Label>
                <Select value={editingStaff.role} onValueChange={(value) => setEditingStaff({...editingStaff, role: value, outlet_ids: value === 'admin' ? [] : editingStaff.outlet_ids})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="admin">Admin (Full Access)</SelectItem>
                    <SelectItem value="manager">Manager (POS + Kitchen + Admin)</SelectItem>
                    <SelectItem value="pos">POS Staff</SelectItem>
                    <SelectItem value="kitchen">Kitchen Staff (KOT)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">{getRoleDescription(editingStaff.role)}</p>
              </div>
              
              {/* Location Selection - Only for non-admin roles */}
              {editingStaff.role !== 'admin' && (
                <div>
                  <Label className="text-white mb-2 block">Location Access</Label>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                    {outlets.length === 0 ? (
                      <p className="text-gray-500 text-sm">No outlets available</p>
                    ) : (
                      outlets.map(outlet => (
                        <div key={outlet.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-outlet-${outlet.id}`}
                            checked={editingStaff.outlet_ids?.includes(outlet.id)}
                            onCheckedChange={() => toggleOutletSelection(outlet.id, false)}
                            className="border-white/30"
                          />
                          <label
                            htmlFor={`edit-outlet-${outlet.id}`}
                            className="text-sm text-gray-300 cursor-pointer flex-1"
                          >
                            {outlet.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-white mb-2 block">Phone</Label>
                <Input
                  value={editingStaff.phone}
                  onChange={(e) => setEditingStaff({...editingStaff, phone: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={editingStaff.email || ''}
                  onChange={(e) => setEditingStaff({...editingStaff, email: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">New Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={editingStaff.password || ''}
                  onChange={(e) => setEditingStaff({...editingStaff, password: e.target.value || undefined})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="••••••••"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit-active"
                  checked={editingStaff.active}
                  onCheckedChange={(checked) => setEditingStaff({...editingStaff, active: checked})}
                  className="border-white/30"
                />
                <Label htmlFor="edit-active" className="text-white cursor-pointer">Active</Label>
              </div>
              <Button
                onClick={handleEditStaff}
                className="w-full bg-[#00C853] hover:bg-[#00E676] text-white py-6 text-lg font-bold"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;
